if (!customElements.get('takt-search-connector')) {
  customElements.define('takt-search-connector', class extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      this.connections = new Map();
      this.initialize = this.initialize.bind(this);
      const options = { signal: this.events.signal };
      document.addEventListener('shopify:section:load', this.initialize, options);
      document.addEventListener('shopify:section:unload', event => {
        for (const [input, cleanup] of this.connections) {
          if (event.target.contains(input)) {
            cleanup();
            this.connections.delete(input);
          }
        }
      }, options);
      this.initialize();
    }

    initialize() {
      for (const [input, cleanup] of this.connections) {
        if (!input.isConnected) {
          cleanup();
          this.connections.delete(input);
        }
      }
      document.querySelectorAll('[data-takt-search-input]').forEach((input, index) => {
        if (this.connections.has(input) || input.dataset.taktPredictiveReady) return;
        const form = input.form;
        if (!form) return;
        const container = form.parentElement;
        const listeners = new AbortController();
        const options = { signal: listeners.signal };
        const restoredAttributes = new Map();
        const setAttribute = (element, key, value) => {
          if (!restoredAttributes.has(element)) restoredAttributes.set(element, new Map());
          if (!restoredAttributes.get(element).has(key)) restoredAttributes.get(element).set(key, element.getAttribute(key));
          element.setAttribute(key, value);
        };
        input.dataset.taktPredictiveReady = 'true';
        setAttribute(form, 'autocomplete', 'off');
        setAttribute(input, 'autocomplete', 'off');
        const results = document.createElement('div');
        results.className = 'takt-search__suggestions';
        results.hidden = true;
        form.after(results);
        const live = document.createElement('span');
        live.className = 'visually-hidden';
        live.setAttribute('role', 'status');
        live.setAttribute('aria-live', 'polite');
        form.after(live);
        const listId = 'TAKTSearchSuggestions-' + this.dataset.section + '-' + index;
        setAttribute(input, 'role', 'combobox');
        setAttribute(input, 'aria-autocomplete', 'list');
        setAttribute(input, 'aria-haspopup', 'listbox');
        setAttribute(input, 'aria-controls', listId);
        setAttribute(input, 'aria-expanded', 'false');
        const catalog = this.querySelector('[data-search-catalog]');
        const features = document.getElementById('shopify-features');
        let predictiveSupported = true;
        try { predictiveSupported = JSON.parse(features?.textContent || '{}').predictiveSearch !== false; } catch {}
        let timer;
        let request;
        let current = -1;
        let sequence = 0;
        const reveal = () => {
          current = -1;
          input.removeAttribute('aria-activedescendant');
          input.setAttribute('aria-expanded', 'true');
          results.hidden = false;
        };
        const renderLocal = query => {
          const list = document.createElement('ul');
          list.setAttribute('role', 'listbox');
          list.id = listId;
          const words = query.toLocaleLowerCase().split(/\s+/).filter(Boolean);
          [...(catalog?.content.querySelectorAll('[data-search-text]') || [])]
            .filter(item => words.every(word => item.dataset.searchText.toLocaleLowerCase().replaceAll('-', ' ').includes(word)))
            .slice(0, 6).forEach((item, itemIndex) => {
              const result = item.cloneNode(true);
              result.id = listId + '-local-' + itemIndex;
              result.removeAttribute('data-search-text');
              list.append(result);
            });
          const more = document.createElement('li');
          more.setAttribute('role', 'option');
          more.setAttribute('aria-selected', 'false');
          more.id = listId + '-all';
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'predictive-search__item';
          button.textContent = this.dataset.searchLabel + ' “' + query + '”';
          button.addEventListener('click', () => form.requestSubmit(), options);
          more.append(button);
          list.append(more);
          results.replaceChildren(list);
          reveal();
        };
        const close = () => {
          sequence++;
          clearTimeout(timer);
          request?.abort();
          results.hidden = true;
          input.setAttribute('aria-expanded', 'false');
          input.removeAttribute('aria-activedescendant');
          results.querySelectorAll('[aria-selected]').forEach(option => option.setAttribute('aria-selected', 'false'));
          current = -1;
        };
        const load = async () => {
          const query = input.value.trim();
          if (!query) { close(); return; }
          const attempt = ++sequence;
          request?.abort();
          if (!predictiveSupported) { renderLocal(query); return; }
          const active = new AbortController();
          request = active;
          try {
            const url = new URL(this.dataset.endpoint, location.origin);
            url.search = new URLSearchParams({ q: query, 'resources[type]': 'product', 'resources[limit]': '6', section_id: 'predictive-search' });
            const response = await fetch(url, { signal: active.signal });
            if (!response.ok) throw new Error('Search unavailable');
            const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
            if (active.signal.aborted || attempt !== sequence || !input.isConnected || input.value.trim() !== query) return;
            const section = parsed.querySelector('#shopify-section-predictive-search');
            const list = section?.querySelector('[role="listbox"]');
            if (!list) throw new Error('Search results unavailable');
            results.replaceChildren(...section.childNodes);
            list.id = listId;
            results.querySelectorAll('[role="option"]').forEach((option, optionIndex) => { option.id = listId + '-remote-' + optionIndex; });
            results.querySelectorAll('a').forEach(link => { link.tabIndex = 0; });
            results.querySelectorAll('button').forEach(button => button.addEventListener('click', () => form.requestSubmit(), options));
            reveal();
            live.textContent = results.querySelector('[data-predictive-search-live-region-count-value]')?.textContent.trim() || '';
          } catch (error) {
            if (error.name !== 'AbortError' && attempt === sequence && input.isConnected && input.value.trim() === query) renderLocal(query);
          }
        };
        input.addEventListener('input', () => {
          close();
          results.replaceChildren();
          live.textContent = '';
          if (!input.value.trim()) return;
          timer = setTimeout(load, 220);
        }, options);
        input.addEventListener('keydown', event => {
          if (event.key === 'Escape') { event.preventDefault(); close(); return; }
          if (results.hidden) return;
          const items = [...results.querySelectorAll('[role="option"]')];
          if (!items.length) return;
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            current = current < 0 ? (event.key === 'ArrowDown' ? 0 : items.length - 1) : (current + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
            items.forEach((item, itemIndex) => item.setAttribute('aria-selected', String(itemIndex === current)));
            input.setAttribute('aria-activedescendant', items[current].id);
            items[current].scrollIntoView({ block: 'nearest' });
          } else if (event.key === 'Enter' && current >= 0) {
            event.preventDefault();
            (items[current].matches('a,button') ? items[current] : items[current].querySelector('a,button'))?.click();
          }
        }, options);
        container.querySelector('[data-takt-search-close]')?.addEventListener('click', close, options);
        input.addEventListener('focus', () => {
          if (input.value.trim() && results.childNodes.length) {
            results.hidden = false;
            input.setAttribute('aria-expanded', 'true');
          }
        }, options);
        container.addEventListener('focusout', event => {
          if (!container.contains(event.relatedTarget)) close();
        }, options);
        this.connections.set(input, () => {
          close();
          listeners.abort();
          delete input.dataset.taktPredictiveReady;
          for (const [element, attributes] of restoredAttributes) {
            for (const [key, value] of attributes) {
              if (value === null) element.removeAttribute(key);
              else element.setAttribute(key, value);
            }
          }
          results.remove();
          live.remove();
        });
      });
    }

    disconnectedCallback() {
      this.events?.abort();
      for (const cleanup of this.connections?.values() || []) cleanup();
      this.connections?.clear();
      this.events = null;
    }
  });
}

